// StickiesListPanelMain.js - Updated to support tag updates
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import './StickiesListPanelMain.css';
import AICompositionModal from '../AICompositionModal';
import StickiesTable from './StickiesTable';
import { sendStickiesToAIComposition, COMPOSITION_MODES } from '../../api/aiComposition';

const StickiesListPanelMain = ({ 
  stickies, 
  onExpandSticky, 
  displayModes = {},
  onToggleDisplayMode,
  selectedStickies = {}, // Changed from pinnedStickies
  onSelectChange, // New prop for handling selection changes
  onSaveLayout = () => console.warn('onSaveLayout not provided'),
  onRemoveSticky = () => console.warn('onRemoveSticky not provided'),
  onReorderSticky = () => console.warn('onReorderSticky not provided'),
  updateSticky = () => console.warn('updateSticky not provided'),
  isRightPanelVisible = false
}) => {
  const [height, setHeight] = useState(30);
  const [isResizing, setIsResizing] = useState(false);
  const startResizeHeight = useRef(0);
  const startResizeY = useRef(0);
  const previousHeight = useRef(150);
  const [freezeButtonText, setFreezeButtonText] = useState('Freeze');
  const [isAICompositionModalOpen, setIsAICompositionModalOpen] = useState(false);
  

  // Memoize visible stickies to prevent unnecessary re-renders
  const visibleStickies = useMemo(() => {
    return stickies
      .filter(sticky => 
        !displayModes[sticky.id] || 
        displayModes[sticky.id] === 'list' || 
        displayModes[sticky.id] === 'both'
      )
      .sort((a, b) => (a.list_order || 0) - (b.list_order || 0));
  }, [stickies, displayModes]);

  const MIN_HEIGHT = 30;
  const MAX_HEIGHT = 300;

  // Reorder handler
  const handleReorderSticky = useCallback((stickyId, direction) => {
    const currentIndex = visibleStickies.findIndex(s => s.id === stickyId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= visibleStickies.length) return;

    const reorderedStickies = [...visibleStickies];
    const [movedSticky] = reorderedStickies.splice(currentIndex, 1);
    reorderedStickies.splice(newIndex, 0, movedSticky);

    const updates = reorderedStickies.map((sticky, index) => ({
      id: sticky.id,
      list_order: index
    }));

    onReorderSticky(updates);
  }, [visibleStickies, onReorderSticky]);

  // Title update handler
  const handleTitleUpdate = useCallback((stickyId, newTitle) => {
    updateSticky(stickyId, { title: newTitle });
  }, [updateSticky]);

  // NEW: Tag update handler
  const handleTagUpdate = useCallback((stickyId, tagIndex, newTag) => {
    const tagField = tagIndex === 0 ? 'tag1' : 'tag2';
    updateSticky(stickyId, { [tagField]: newTag });
    console.log(`Updated ${tagField} for sticky ${stickyId} to: ${newTag}`);
  }, [updateSticky]);

  // Resize handlers (keeping existing)
  const handleHeaderDoubleClick = useCallback(() => {
    if (height > MIN_HEIGHT) {
      previousHeight.current = height;
      setHeight(MIN_HEIGHT);
    } else {
      setHeight(Math.min(previousHeight.current, MAX_HEIGHT));
    }
  }, [height]);

  const handleResize = useCallback((e) => {
    if (!isResizing) return;
    const deltaY = startResizeY.current - e.clientY;
    const newHeight = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, startResizeHeight.current + deltaY));
    setHeight(newHeight);
  }, [isResizing]);

  const handleResizeStart = useCallback((e) => {
    setIsResizing(true);
    startResizeHeight.current = height;
    startResizeY.current = e.clientY;
    document.body.classList.add('list-panel-resizing-main');
  }, [height]);

  const handleResizeEnd = useCallback(() => {
    if (isResizing) {
      setIsResizing(false);
      document.body.classList.remove('list-panel-resizing-main');
    }
  }, [isResizing]);

  // Other handlers (keeping existing)
  const handleFreezeAll = useCallback(() => {
    const stickyUpdates = stickies.map(sticky => ({
      id: sticky.id,
      x: sticky.x,
      y: sticky.y,
      width: sticky.width,
      height: sticky.height,
      main_x: sticky.main_x,
      main_y: sticky.main_y,
      main_width: sticky.main_width,
      main_height: sticky.main_height,
      display_mode: displayModes[sticky.id] || 'both',
      z_index: sticky.zIndex
    }));

    setFreezeButtonText('Saved!');
    setTimeout(() => {
      setFreezeButtonText('Freeze');
    }, 2000);
    
    onSaveLayout(stickyUpdates);
  }, [stickies, displayModes, onSaveLayout]);

  const handleMinimizeAll = useCallback(() => {
  stickies.forEach(sticky => {
    if (!selectedStickies[sticky.id] && sticky.note_id) { // Changed from pinnedStickies
      onToggleDisplayMode(sticky.id, 'list');
    }
  });
}, [stickies, selectedStickies, onToggleDisplayMode]); // Changed from pinnedStickies

  const handleStickyClick = useCallback((sticky) => {
    if (displayModes[sticky.id] === 'list') {
      onToggleDisplayMode(sticky.id, 'both');
    }
    onExpandSticky(sticky.id);
  }, [displayModes, onToggleDisplayMode, onExpandSticky]);

 const handleStickySelect = useCallback((stickyId, isSelected) => {
  // Use the parent's onSelectChange instead of local state
  onSelectChange(stickyId, isSelected);
}, [onSelectChange]);

  const getSelectedStickyIdsArray = useCallback(() => {
  const selectedIds = Object.entries(selectedStickies)
    .filter(([_, isSelected]) => isSelected)
    .map(([id]) => id);
  
  return selectedIds;
}, [selectedStickies]);

  const handleAICompositionClick = useCallback(() => {
    setIsAICompositionModalOpen(true);
  }, []);

const handleSendToAI = useCallback(async (selectedStickies, mode = COMPOSITION_MODES.COMPOSE_NOTE) => {
  try {
    console.log('Sending to AI composition:', selectedStickies);
    
    // Get the actual sticky objects with domain/tag data
    const stickiesWithTags = stickies.filter(sticky => 
      selectedStickies.includes(sticky.id)
    );
    
    const noteContext = {
      note_id: 1, // You'll need to get this from your note context
      note_title: "AI Composition Result" // Or get from current note
    };
    
    const result = await sendStickiesToAIComposition(
      stickiesWithTags, 
      mode, 
      noteContext
    );
    
    console.log('AI Composition Result:', result);
    
    // TODO: Insert structured result into note
    // You'll handle the structured JSON here (title, summary, claims, etc.)
    
    setIsAICompositionModalOpen(false);
    
  } catch (error) {
    console.error('AI composition failed:', error);
    // Show error toast to user
    alert(`AI composition failed: ${error.message}`);
  }
}, [stickies]);

  const handleDeleteSelected = useCallback(async () => {
  const selectedIds = getSelectedStickyIdsArray();
  if (selectedIds.length === 0) return;
  
  if (window.confirm(`Delete ${selectedIds.length} selected stickies? This cannot be undone.`)) {
    for (const stickyId of selectedIds) {
      try {
        await onRemoveSticky(stickyId);
      } catch (error) {
        console.error(`Failed to delete sticky ${stickyId}:`, error);
      }
    }
    // Clear selections after deletion
    selectedIds.forEach(id => onSelectChange(id, false));
  }
}, [getSelectedStickyIdsArray, onRemoveSticky, onSelectChange]);

  const getStickyIcon = useCallback((type) => {
    switch (type) {
      case 'ai': return '🤖';
      case 'checklist': return '☑️';
      default: return '📝';
    }
  }, []);

  // Effects
  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', handleResize);
      window.addEventListener('mouseup', handleResizeEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleResize);
      window.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [isResizing, handleResize, handleResizeEnd]);

  return (
    <div 
      className={`stickies-list-panel-main ${isRightPanelVisible ? 'right-panel-visible' : 'panel-closed'}`}
      style={{ height: `${height}px` }}
    >
      <div 
        className="list-header-main list-panel-resize-header-main"
        onMouseDown={handleResizeStart}
        onDoubleClick={handleHeaderDoubleClick}
      >
        <div className="list-panel-resize-handle-main" />
        <h3>Sticky Notes ({visibleStickies.length})</h3>
        <button 
          className="minimize-all-button-main"
          onClick={handleMinimizeAll}
          title="Minimize all unpinned stickies"
        >
          Minimize All
        </button>
        <button
          className="freeze-all-button-main"
          onClick={handleFreezeAll}
          title="Save current sticky positions and states"
        >
          {freezeButtonText}
        </button>
        <button 
          className="ai-composition-button-main"
          onClick={handleAICompositionClick}
          disabled={getSelectedStickyIdsArray().length === 0}
          title="Create AI composition from selected stickies"
        >
          AI Composition ({getSelectedStickyIdsArray().length})
        </button>
        <button 
            className="delete-selected-button-main"
            onClick={handleDeleteSelected}
            disabled={getSelectedStickyIdsArray().length === 0}
            title={`Delete ${getSelectedStickyIdsArray().length} selected stickies`}
          >
            Delete ({getSelectedStickyIdsArray().length})
          </button>
      </div>
      
      {/* Updated table component with tag support */}
      <StickiesTable
        stickies={visibleStickies}
        selectedStickies={selectedStickies}
        displayModes={displayModes}
        onStickyClick={handleStickyClick}
        onStickySelect={handleStickySelect}
        onReorderSticky={handleReorderSticky}
        onTitleUpdate={handleTitleUpdate} 
        onTagUpdate={handleTagUpdate}
        getStickyIcon={getStickyIcon}
      />

      {/* Keep existing modal */}
      <AICompositionModal
        isOpen={isAICompositionModalOpen}
        onClose={() => setIsAICompositionModalOpen(false)}
        stickies={stickies}
        selectedStickyIds={getSelectedStickyIdsArray()}
        onSendToAI={handleSendToAI}
      />
    </div>
  );
};

export default StickiesListPanelMain;